import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { Helmet } from 'react-helmet-async';

import { styled } from '@mui/material/styles';
import { Card, Container } from '@mui/material';

import { marked } from 'marked';
import { useTranslation } from 'react-i18next';

import CurrencyConverter from 'components/CurrencyConverter'
import servicesContext from 'servicesContext';

import useCurrencyURLParams from './useCurrencyURLParams';

const { currencyExchangeService } = servicesContext;

const StyledContent = styled('div')({
    maxWidth: 900,
    margin: 'auto',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    marginBottom: 30,
    marginTop: 20,
});

const getExchangeRate = async (baseCurrency, targetCurrency) => (
    currencyExchangeService.getExchangeRate(baseCurrency, targetCurrency)
);

const convertMarkdownToHtml = (markdownText) => (
    marked.parse(markdownText)
);

export default function CurrencyPairPage() {

    const { t } = useTranslation();
    const navigate = useNavigate();

    // get the currencies from the path
    const { currencyPair } = useParams();
    const [pathFromCurrency, setPathFromCurrency] = useState(null);
    const [pathToCurrency, setPathToCurrency] = useState(null);

    useEffect(() => {

        const [pathFromCurrency, pathToCurrency] = currencyPair.split('-');
        if (!pathFromCurrency || !pathToCurrency) {
            navigate('/404');
        } else {
            setPathFromCurrency(pathFromCurrency.toUpperCase());
            setPathToCurrency(pathToCurrency.toUpperCase());
        }

    }, [ currencyPair, navigate ]);

    // get the currencies from the URL
    const {
        amount: urlAmount, fromCurrency: urlFromCurrency, toCurrency: urlToCurrency,
        updateAmount, updateFromCurrency, updateToCurrency,
    } = useCurrencyURLParams();


    // get the final currencies
    const [fromCurrency, setFromCurrency] = useState(null);
    const [toCurrency, setToCurrency] = useState(null);

    useEffect(() => {

        const fromCurrency = urlFromCurrency || pathFromCurrency;
        const toCurrency = urlToCurrency || pathToCurrency;

        if (fromCurrency && toCurrency) {
            setFromCurrency(fromCurrency);
            setToCurrency(toCurrency);
        } else {
            setFromCurrency(null);
            setToCurrency(null);
        }

    }, [ urlFromCurrency, urlToCurrency, pathFromCurrency, pathToCurrency ]);

    const [currencies, setCurrencies] = useState(null);
    const [currencyPairInsight, setCurrencyPairInsight] = useState(null);

    useEffect(() => {

        const fetchCurrencies = async () => {
            const currencies = await currencyExchangeService.getSupportedCurrencies();
            setCurrencies(currencies);
        };

        fetchCurrencies();
    }, []);

    useEffect(() => {

        const fetchCurrencies = async () => {
            const { insight } = await currencyExchangeService.getCurrencyPairInsight(
                pathFromCurrency.toUpperCase(), pathToCurrency.toUpperCase(),
            );
            const insightMarkdown = JSON.parse(insight).content;
            const insightHtml = convertMarkdownToHtml(insightMarkdown)
            setCurrencyPairInsight(insightHtml);
        };

        if (pathFromCurrency && pathToCurrency) {
            fetchCurrencies();
        }
    }, [pathFromCurrency, pathToCurrency]);

    return (
        <>
            {fromCurrency && toCurrency &&
                <Helmet>
                    <title>{`AI Exchange – Simple & Smart Currency Conversion with AI Insights for ${fromCurrency}-${toCurrency} pair`}</title>
                </Helmet>        
            }

            <Container>
                <StyledContent sx={{ textAlign: 'center', alignItems: 'center' }}>
                    <Card sx={{ width: '100%', px: 1, py: 4, mt: 2 }}>
                        {currencies && fromCurrency && toCurrency &&
                            <CurrencyConverter
                                key={`${fromCurrency}-${toCurrency}`}
                                currencies={currencies}
                                initialAmount={urlAmount}
                                initialFromCurrency={fromCurrency}
                                initialToCurrency={toCurrency}
                                onAmountChange={updateAmount}
                                onFromCurrencyChange={updateFromCurrency}
                                onToCurrencyChange={updateToCurrency}
                                getExchangeRate={getExchangeRate}
                            />
                        }
                    </Card>
                    <Card sx={{ width: '100%', px: 6, py: 4, mt: 4, textAlign: 'left' }}>
                        <h2>{t('insightsPanel.panelTitle')}</h2>
                        {currencyPairInsight &&
                            <>
                                {/* eslint-disable-next-line react/no-danger */}
                                <div dangerouslySetInnerHTML={{ __html: currencyPairInsight }} />
                                <div style={{ fontStyle: 'italic' }}>
                                    {t('insightsPanel.legalDisclaimer')}
                                </div>
                            </>
                        }
                    </Card>
                </StyledContent>
            </Container>
        </>
    );
}
